import { useTranslation } from "react-i18next";
import { useTheme } from "../../../context/ThemeContext";
import type { BudgetPeriodAnalysis } from "../lib/budgetPeriod";
import { categoryStatus } from "../lib/budgetTimeline";
import { CATEGORY_COLORS, categoryLabel, money, monthLabel } from "../lib/format";
import { ui } from "../theme";
import { ProgressBar, STATUS_TONES } from "./BudgetCharts";
import { Chip, Kpi } from "./ui";

// Mes a mes del periodo: los meses cerrados comparan presupuesto contra lo
// gastado; el mes en curso y los que vienen muestran la tendencia, el mismo mes
// del año pasado y un pronóstico con su % de certeza (lib/budgetPeriod). El
// periodo lo fija el filtro de la página (#1332); aquí solo se pinta.

const certaintyTone = (pct: number) => (pct >= 70 ? "bg-emerald-500/15 text-emerald-600" : pct >= 45 ? "bg-amber-500/15 text-amber-600" : "bg-red-500/15 text-red-600");

export default function BudgetPeriodCard({ analysis: a }: { analysis: BudgetPeriodAnalysis }) {
  const { t, i18n } = useTranslation("common");
  const { isDarkMode } = useTheme();
  const c = ui(isDarkMode);
  const language = i18n.language;
  const m = (v: number | null | undefined) => (v == null ? "—" : money(v, "MXN", language));
  const tp = (k: string, o?: Record<string, unknown>) => t(`expenses.budgets.periodAnalysis.${k}`, o);

  return (
    <div className={c.card} data-testid="budget-period">
      <div className="mb-3">
        <h2 className={`text-sm font-semibold ${c.text}`}>{tp("title")}</h2>
        <p className={`text-xs ${c.muted}`}>{tp("subtitle")}</p>
      </div>

      {
        <div>
          <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
            <Kpi label={tp("kpi.budget")} value={m(a.past.budget + a.future.budget)} hint={tp("kpi.months", { count: a.months.length })} />
            <Kpi
              label={tp("kpi.actual")}
              value={a.past.months ? m(a.past.actual) : "—"}
              hint={a.past.months ? tp("kpi.actualHint", { count: a.past.months, pct: a.past.pct == null ? "—" : `${a.past.pct.toFixed(0)}%` }) : tp("kpi.noPast")}
              tone={a.past.months && a.past.difference < 0 ? "bad" : undefined}
            />
            <Kpi
              label={tp("kpi.forecast")}
              value={a.future.months ? m(a.future.forecast) : "—"}
              hint={a.future.months ? (a.future.forecast != null ? tp("kpi.forecastHint", { low: m(a.future.low), high: m(a.future.high) }) : tp("kpi.noHistory")) : tp("kpi.noFuture")}
              tone={a.future.difference != null && a.future.difference < 0 ? "bad" : undefined}
            />
            <Kpi
              label={tp("kpi.certainty")}
              value={a.future.certainty == null ? "—" : `${a.future.certainty}%`}
              hint={a.future.months ? tp(a.hasLastYear ? "kpi.certaintyHintBoth" : "kpi.certaintyHintTrend", { count: a.historyMonths }) : tp("kpi.noFuture")}
              tone={a.future.certainty == null ? undefined : a.future.certainty >= 70 ? "good" : a.future.certainty < 45 ? "bad" : undefined}
            />
          </div>

          <div className="mt-4 overflow-x-auto">
            <table className="w-full min-w-[860px] text-sm" aria-label={tp("monthsTitle")}>
              <thead>
                <tr className={`text-left text-xs ${c.muted}`}>
                  <th className="px-2 py-2">{tp("col.month")}</th>
                  <th className="px-2 py-2 text-right">{t("expenses.budgets.budgeted")}</th>
                  <th className="px-2 py-2 text-right">{tp("col.actual")}</th>
                  <th className="px-2 py-2 text-right">{tp("col.trend")}</th>
                  <th className="px-2 py-2 text-right">{tp("col.lastYear")}</th>
                  <th className="px-2 py-2 text-right">{tp("col.forecast")}</th>
                  <th className="px-2 py-2 text-right">{t("expenses.budgets.difference")}</th>
                  <th className="px-2 py-2">{tp("col.certainty")}</th>
                </tr>
              </thead>
              <tbody>
                {a.months.map((x) => (
                  <tr key={x.period} className={`border-t ${c.border} ${c.text}`} data-testid={`period-${x.period}`}>
                    <td className="whitespace-nowrap px-2 py-2">
                      {monthLabel(x.period, language)}
                      <Chip className={`ml-2 ${x.phase === "past" ? "bg-slate-500/15 text-slate-500" : x.phase === "current" ? "bg-teal-500/15 text-teal-600" : "bg-indigo-500/15 text-indigo-500"}`}>{tp(`phase.${x.phase}`)}</Chip>
                    </td>
                    <td className="px-2 py-2 text-right tabular-nums">{x.budget ? m(x.budget) : "—"}</td>
                    <td className="px-2 py-2 text-right tabular-nums">{m(x.actual)}</td>
                    <td className={`px-2 py-2 text-right tabular-nums ${c.muted}`}>{m(x.trend)}</td>
                    <td className={`px-2 py-2 text-right tabular-nums ${c.muted}`} title={x.seasonal != null ? tp("lastYearAdjusted", { value: m(x.seasonal) }) : undefined}>
                      {m(x.lastYear)}
                    </td>
                    <td className="px-2 py-2 text-right font-medium tabular-nums" title={x.low != null ? tp("kpi.forecastHint", { low: m(x.low), high: m(x.high) }) : undefined}>
                      {m(x.forecast)}
                    </td>
                    <td className={`px-2 py-2 text-right tabular-nums ${x.difference != null && x.difference < 0 ? "text-red-500" : ""}`}>{m(x.difference)}</td>
                    <td className="px-2 py-2">{x.certainty != null ? <Chip className={certaintyTone(x.certainty)}>{`${x.certainty}%`}</Chip> : <span className={c.muted}>—</span>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="mt-4 grid gap-4 lg:grid-cols-2">
            {a.past.byCategory.length > 0 && (
              <div data-testid="period-past">
                <h3 className={`mb-2 text-xs font-semibold ${c.text}`}>{tp("pastTitle", { count: a.past.months })}</h3>
                {a.past.byCategory.map((r) => {
                  const status = categoryStatus(r.budget, r.actual);
                  return (
                    <div key={r.category} className="mb-2">
                      <div className={`flex items-center justify-between gap-2 text-xs ${c.text}`}>
                        <span className="inline-flex items-center gap-2">
                          <span className="h-2.5 w-2.5 rounded-full" style={{ background: CATEGORY_COLORS[r.category] }} />
                          {categoryLabel(r.category, language)}
                        </span>
                        <span className="tabular-nums">
                          {m(r.actual)} <span className={c.muted}>/ {r.budget ? m(r.budget) : t("expenses.budgets.unbudgeted")}</span>
                          {r.pct != null && <Chip className={`ml-2 ${STATUS_TONES[status].chip}`}>{`${r.pct.toFixed(0)}%`}</Chip>}
                        </span>
                      </div>
                      <ProgressBar pct={r.pct} status={status} label={categoryLabel(r.category, language)} />
                    </div>
                  );
                })}
              </div>
            )}
            {a.future.byCategory.length > 0 && (
              <div data-testid="period-future">
                <h3 className={`mb-2 text-xs font-semibold ${c.text}`}>{tp("futureTitle", { count: a.future.months })}</h3>
                {a.future.byCategory.map((r) => {
                  const status = categoryStatus(r.budget, r.forecast);
                  return (
                    <div key={r.category} className="mb-2">
                      <div className={`flex items-center justify-between gap-2 text-xs ${c.text}`}>
                        <span className="inline-flex items-center gap-2">
                          <span className="h-2.5 w-2.5 rounded-full" style={{ background: CATEGORY_COLORS[r.category] }} />
                          {categoryLabel(r.category, language)}
                        </span>
                        <span className="tabular-nums">
                          {m(r.forecast)} <span className={c.muted}>/ {r.budget ? m(r.budget) : t("expenses.budgets.unbudgeted")}</span>
                          {r.pct != null && <Chip className={`ml-2 ${STATUS_TONES[status].chip}`}>{`${r.pct.toFixed(0)}%`}</Chip>}
                        </span>
                      </div>
                      <ProgressBar pct={r.pct} status={status} label={categoryLabel(r.category, language)} />
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {a.future.months > 0 && (
            <p className={`mt-3 text-xs ${c.muted}`}>
              <i className="ri-information-line mr-1" />
              {tp("method", { growth: a.growth == null ? "—" : `×${a.growth.toFixed(2)}` })}
            </p>
          )}
        </div>
      }
    </div>
  );
}
